import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import axios from "axios";
import cookies from "next-cookies";
import { jwtDecode } from "jwt-decode";
import Constants from "const/constants";
import Persistence from "pages/common/lib/grabyo-persistence";
import { serializeQueryParameters } from "util/string-utils";
import { isProdEnvironment } from "util/env-utils";
import {
    addTokenExpiryInterceptor,
    addErrorLogsFilterInterceptor
} from "util/auth-utils";
import { isServer } from "util/browser-utils";
import type {
    GetServerSidePropsContext,
    NextPageContext,
    PreviewData
} from "next";
import type { GrabyoJWTPayload } from "api/GrabyoJWTPayload";
import type { Scope } from "pages/common/key-bindings/types/Scope.enum";
import type { ApiDeviceProfileResponse } from "./device-profiles";
import type { OrganizationPreferences } from "pages/producer/common/util/producer-page-utils";
import type { Organization } from "./organizations.types";
import type { Stream } from "./Stream.types";
import type { LoggedInUser, User } from "./User.types";
import type { SocialCredential } from "./broadcast.types";
import type { RTMPCredential } from "./integrations.types";
import type { PlatformConfig } from "./PlatformConfig.types";
import type { Grab } from "./Grab.types";
import type { ParsedUrlQuery } from "querystring";
import { sha256 } from "util/hash-utils";

/**
 * May need to update this utility type when we move away from the custom Express server
 */
type Cookies = Record<string, string>;

/**
 * @summary - Convert a map of cookies to a header string
 * @param cookies - Map of cookies
 * @example
 * serializeCookies({ "some-cookie": "cookie-value", "another-cookie": "another value" });
 * // "some-cookie=cookie-value; another-cookie=another value; "
 */
export const serializeCookies = (cookies: Cookies = {}): string =>
    Object.entries(cookies)
        .map(([cookie, value]) => `${cookie}=${value}; `)
        .join(``);

export const PROJECTS_ENDPOINT = `/ws/composers/editorprojects`;

export const CAMPAIGNS_ENDPOINT = `/ws/campaigns`;

const { OK, MULTIPLE_CHOICES } = Constants.HTTP_STATUS_CODES;

type Context =
    | NextPageContext
    | GetServerSidePropsContext<ParsedUrlQuery, PreviewData>;

export const getHostNameFromRequestContext = (ctx: Context): string =>
    (ctx.req.headers[`x-forwarded-host`] as string).split(`:`)[0];

export class GrabyoApi {
    ctx: Context;
    hostname: string;
    cookies: Record<string, string>;
    baseDomain: string;
    caller: AxiosInstance;

    constructor(ctx?: Context) {
        const options: AxiosRequestConfig = {
            timeout: 300000,
            validateStatus: (status) => {
                return status >= OK && status < MULTIPLE_CHOICES;
            },
            withCredentials: true,
            proxy: {
                host: `127.0.0.1`,
                port: 80
            },
            headers: {
                post: {
                    [`Content-Type`]: `application/x-www-form-urlencoded`
                }
            }
        };

        const apiBasePath = `/api/`;

        if (ctx?.req) {
            this.ctx = ctx;
            const { req } = ctx;

            const { host } = req.headers;
            const protocol = req.headers["X-Forwarded-Proto"] || `http`;

            options.baseURL = `${protocol}://${host}${apiBasePath}`;
            this.baseDomain = `${protocol}://${host}`;

            const parsedCookies = cookies(ctx);
            this.cookies = parsedCookies;
            options.headers.cookie = serializeCookies(this.cookies);

            this.hostname = getHostNameFromRequestContext(ctx);
            options.headers[`x-forwarded-host`] = this.hostname;
        } else {
            options.baseURL = apiBasePath;
        }
        this.caller = axios.create(options);

        this.addErrorLogsFilterInterceptor();
    }

    addErrorLogsFilterInterceptor = () =>
        isServer() && addErrorLogsFilterInterceptor(this.caller);

    addTokenExpiryInterceptor = () => addTokenExpiryInterceptor(this.caller);

    setCookies = (newCookies = {}) => {
        this.cookies = {
            ...this.cookies,
            ...newCookies
        };
        this.caller.defaults.headers.cookie = serializeCookies(this.cookies);
    };

    get jwtPayload(): GrabyoJWTPayload | null {
        const jwtCookie = this.cookies?.["grabyo-jwt"];
        if (this.ctx && jwtCookie) {
            const decodedJwtPayload = jwtDecode<GrabyoJWTPayload>(jwtCookie);
            return decodedJwtPayload || null;
        }
        return null;
    }

    get organizationIdFromJWT(): string | null {
        return this.jwtPayload?.organizationId || null;
    }

    // TODO: type campaigns response
    getCampaigns = async (pageSize = 20) => {
        try {
            const response = await this.get(
                `${CAMPAIGNS_ENDPOINT}?pageSize=${pageSize}`
            );
            return response;
        } catch (err) {
            return {
                data: []
            };
        }
    };

    // TODO: type streams response
    getStreams = async (): Promise<Stream[]> => {
        const { data } = await this.get(`/v1/streams`);
        return data;
    };

    // TODO: type stream response
    getStream = async (streamId: string): Promise<Stream> => {
        return this.get(`/v1/streams/${streamId}`).then((res) => res.data.data);
    };

    // TODO: type users response
    getUsers = async (): Promise<{ data: User[] }> => {
        try {
            const { data } = await this.get(`v1/users`);
            return data;
        } catch (e) {
            return { data: [] };
        }
    };

    // TODO: type getLoggedInUser response
    getLoggedInUser = async (): Promise<LoggedInUser> => {
        const {
            data: { data }
        } = await this.get(`v1/account`);
        return data;
    };

    // TODO: type getSocialCredentials response
    getSocialCredentials = async (): Promise<SocialCredential[]> => {
        const {
            data: { data }
        } = await this.get(`v1/account/social-credentials`);
        return data;
    };

    // TODO: type getCustomRtmpCredentials response
    getCustomRtmpCredentials = async (): Promise<RTMPCredential[]> => {
        const {
            data: { data }
        } = await this.get(`v1/account/custom-rtmp-credentials`);
        return data;
    };

    // TODO: type getOrganization response
    getOrganization = async (): Promise<{ data: Organization }> =>
        this.get(`/ws/organizations/me`);

    // TODO: type getAllOrganization response
    /**
     * @summary - Get a list of all orgs. You need to be an ADMIN user in the Grabyo Admin org to access this.
     * @returns {Array[Object]} Array of organizations
     */
    getAllOrganizations = async (): Promise<Organization[]> => {
        if (isProdEnvironment) {
            try {
                const {
                    data: { data }
                } = await this.get(`v1/organizations`);
                return data;
            } catch ({ response }) {
                if (response.status !== 403) {
                    console.error(
                        `Failed to get organizations`,
                        response.status
                    );
                    return Promise.reject(response);
                }
            }
        }

        return [];
    };

    // TODO: type login response
    login = async (email: string, password: string) => {
        const hashedPassword = await sha256(password);
        return this.post(`/studiousers/login`, {
            email,
            password: hashedPassword,
            tempLoginSource: `web`,
            sessionless: false,
            donottrack: false
        });
    };

    getPlatformConfig = async (): Promise<PlatformConfig> => {
        const { data }: AxiosResponse<PlatformConfig> = await this.get(
            `/platform-config/api/v1/platformconfig?key=announcements&key=allowSimultaneousFbAndYtLiveBroadcasts&key=displayYoutubeAnalytics&key=defaultInlineTypeConfig&key=producerMaxGuests&key=templateCacheBusterVersion&key=defaultResolution&key=maxConcurrentBroadcastDestinationsPerStream`
        );
        return data;
    };

    // TODO: type getShare response and / or move this to clipper.js as it's only used there
    getShare = async (id: string) => {
        const { data } = await this.get(`ws/shares/${id}`);
        return data;
    };

    // TODO: type getGrab response and / or move this to clipper.js as it's only used there
    getGrab = async (
        id: string,
        extended = false,
        noCache = false
    ): Promise<Grab> => {
        const { data } = await this.get(`ws/grabs/${id}`, {
            params: { extended, noCache }
        });
        return data;
    };

    // TODO: type getProjectsData response
    getProjectsData = async () => {
        try {
            const { data } = await this.caller.get(PROJECTS_ENDPOINT);
            return data;
        } catch (e) {
            console.error(`Failed to get projects`, e);
            return [];
        }
    };

    // TODO: type getProjectData response and / or move this to editor.js as it's only used there
    getProjectData = async (id: string) => {
        const projects = await this.getProjectsData();
        return projects.find((project) => project.id === id);
    };

    // TODO: type refreshToken response
    refreshToken = async () => {
        const { headers, data } = await this.postJSON(`v2/refresh-token`);

        if (this.ctx) {
            // Extract the JWT cookie value from the `Set-Cookie` header
            let jwtCookie = headers[`set-cookie`]
                .find((cookie) => cookie.indexOf(`grabyo-jwt=`) === 0)
                .replace(/^grabyo-jwt=/, ``);

            // Remove secure flag for local development
            if (this.hostname.includes(`localhost`)) {
                jwtCookie = jwtCookie.replace(/Secure;/, ``);
            }

            // set JWT on this object for the rest of the page loading process
            this.setCookies({ "grabyo-jwt": jwtCookie });
            // forward JWT cookie to client
            this.ctx.res.setHeader(
                `Set-Cookie`,
                serializeCookies({ "grabyo-jwt": jwtCookie })
            );
        } else {
            Persistence.PermStore.set(
                `tokenExpiryDate`,
                Date.now() + data.jwtTokenAgeSeconds * 1000
            );
        }
        return data;
    };

    getOrganizationPreferences = async (): Promise<OrganizationPreferences> => {
        try {
            const config = {
                baseURL: `${this.baseDomain}/media-transport/api/`
            };
            const { data } = await this.get(
                `v1/organization/me/preferences`,
                config
            );
            return data;
        } catch (err) {
            console.error(`media transport request failed - `, err);
            return {
                ingestLimit: 0,
                proIngestsEnabled: false,
                regions: [],
                failed: true
            };
        }
    };

    getInitialDeviceProfiles = async (
        scope: Scope
    ): Promise<{
        deviceProfiles?: ApiDeviceProfileResponse[];
        etag?: string;
        unmodified?: boolean;
    }> => {
        // Used in server-side rendering

        const orgId = this.organizationIdFromJWT;
        if (!orgId) {
            return {
                deviceProfiles: []
            };
        }

        try {
            const {
                data,
                headers: { etag }
            } = await this.get(
                `/api/v2/organizations/${orgId}/device-profiles?scope=${scope}`,
                {
                    baseURL: `${this.baseDomain}/user-preferences`
                }
            );
            return {
                deviceProfiles: data,
                etag
            };
        } catch (e) {
            return {
                deviceProfiles: []
            };
        }
    };

    get = (endpoint: string, options?: AxiosRequestConfig) =>
        this.caller.get(endpoint, options);

    post = (endpoint: string, data?: unknown, options?: AxiosRequestConfig) =>
        this.caller.post(
            endpoint,
            serializeQueryParameters(
                data as Parameters<typeof serializeQueryParameters>[0]
            ),
            options
        );

    postJSON = (
        endpoint: string,
        data?: unknown,
        options?: AxiosRequestConfig
    ) =>
        this.caller.post(
            endpoint,
            data,
            Object.assign({}, options, {
                headers: { "Content-Type": `application/json` }
            })
        );

    put = (endpoint: string, data?: unknown, options?: AxiosRequestConfig) =>
        this.caller.put(endpoint, data, options);

    putJSON = (
        endpoint: string,
        data?: unknown,
        options?: AxiosRequestConfig
    ) =>
        this.caller.put(
            endpoint,
            data,
            Object.assign({}, options, {
                headers: { "Content-Type": `application/json` }
            })
        );

    patch = (endpoint: string, data?: unknown, options?: AxiosRequestConfig) =>
        this.caller.patch(
            endpoint,
            serializeQueryParameters(
                data as Parameters<typeof serializeQueryParameters>[0]
            ),
            options
        );

    patchJSON = (
        endpoint: string,
        data?: unknown,
        options?: AxiosRequestConfig
    ) =>
        this.caller.patch(
            endpoint,
            data,
            Object.assign({}, options, {
                headers: { "Content-Type": `application/json` }
            })
        );

    remove = (endpoint: string, options?: AxiosRequestConfig) =>
        this.caller.delete(endpoint, options);
}

export const api = new GrabyoApi();

export const get = api.get;
export const post = api.post;
export const postJSON = api.postJSON;
export const put = api.put;
export const putJSON = api.putJSON;
export const patch = api.patch;
export const patchJSON = api.patchJSON;
export const remove = api.remove;

export const hasJwtCookie = (ctx: Context) =>
    Object.hasOwnProperty.call(cookies(ctx), `grabyo-jwt`);
